/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Divider,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  DataSynced,
  DataSyncFailed,
  DataSyncing,
  EnablePolicyRadioGroup,
  ProvisionOrgInfo,
  StepIcon,
} from './share';
import { Trans, useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import {
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  MultiAppsAuthenticationInfoRes,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '@Types/provision';
import { ProvisionApi } from '@Apis';
import { reportPendo } from '@Utils/setupPendo';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import RejectProvisionModal from './rejectProvisionModal';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import i18next from 'i18next';
import { App, Service } from '@Types/app';

enum OneDriveGraphProvisionStatus {
  NotProvision = 0,
  ProvisioningGrantStep1Done = 1,
  ProvisioningGrantStep2Done = 2,
  Provisioned = 3,
}

const SERVICE = Service.OneDrive;

const OneDriveGraphModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: ProvisionModalState) => {
  const { t } = useTranslation('provision');
  const [errorMsg, setErrorMsg] = useState<ReactNode | undefined>(undefined);
  const getInitStatus = () => {
    if (provisionStatus.status === ProvisionStatusEnum.NotStarted) {
      return OneDriveGraphProvisionStatus.NotProvision;
    } else if (provisionStatus.status === ProvisionStatusEnum.Provisioned) {
      return OneDriveGraphProvisionStatus.Provisioned;
    } else {
      const { apps: appInfo } = provisionStatus?.authorization as {
        apps: {
          app: string;
          status: string;
        }[];
      };
      // first app is onedrive, second app is onedriveBasic
      const [onedriveApp, onedriveBasicApp] = appInfo;
      if (provisionStatus.status === ProvisionStatusEnum.Provisioning) {
        if (
          onedriveApp.status === TokenStatus.NotStarted ||
          onedriveApp.status === TokenStatus.Created
        ) {
          return OneDriveGraphProvisionStatus.ProvisioningGrantStep1Done;
        } else {
          return OneDriveGraphProvisionStatus.ProvisioningGrantStep2Done;
        }
      } else {
        const statusArr = [
          TokenStatus.NotStarted,
          TokenStatus.Failure,
        ] as string[];
        if (statusArr.includes(onedriveApp.status)) {
          return OneDriveGraphProvisionStatus.NotProvision;
        } else if (statusArr.includes(onedriveBasicApp.status)) {
          return OneDriveGraphProvisionStatus.ProvisioningGrantStep1Done;
        } else {
          return OneDriveGraphProvisionStatus.ProvisioningGrantStep2Done;
        }
      }
    }
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [onedriveStatus, setOnedriveStatus] =
    useState<OneDriveGraphProvisionStatus>(getInitStatus());

  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | MultiAppsAuthenticationInfoRes>(null);

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (
      [
        OneDriveGraphProvisionStatus.NotProvision,
        OneDriveGraphProvisionStatus.ProvisioningGrantStep1Done,
      ].includes(onedriveStatus)
    ) {
      ProvisionApi.apiGetMultiAppsAuthenticationInfos(Service.OneDrive, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (
            errorCode === RejectProvisionReason.ServiceConflict ||
            errorCode === RejectProvisionReason.PocConflict
          ) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={SERVICE}
        reason={rejectReason}
      />
    );
  }

  if (
    [
      OneDriveGraphProvisionStatus.NotProvision,
      OneDriveGraphProvisionStatus.ProvisioningGrantStep1Done,
    ].includes(onedriveStatus) &&
    !authenticationInfo
  ) {
    return <></>;
  }

  return (
    <>
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('provision.service.oneDrive.title')}</ModalHeader>

          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <GrantPermissionSteps
              {...{
                onedriveStatus,
                setOnedriveStatus,
                authenticationInfo,
                orgId,
                setErrorMsg,
              }}
            />
            {onedriveStatus ===
              OneDriveGraphProvisionStatus.ProvisioningGrantStep2Done &&
              (syncDataFailFlag ? (
                <DataSyncFailed
                  service={SERVICE}
                  onRetry={async () => {
                    const res = await ProvisionApi.apiRetrySyncData(
                      SERVICE,
                      orgId
                    );
                    if (res.data?.status === 'FAILURE') {
                      if ([813, 814].includes(res.data?.error)) {
                        setErrorMsg(
                          <Trans
                            i18nKey={`provision:provision.service.oneDrive.error.saveInfo.FAILURE;${res.data?.error}`}
                          >
                            placeholder
                            <Link
                              href={getCasOLHLink(
                                CAS_DOC_LINK.provision.oneDrive
                                  .userConfiguration,
                                i18next.language
                              )}
                              target="_blank"
                            />
                            placeholder
                          </Trans>
                        );
                      } else {
                        setErrorMsg(
                          t([
                            `provision.service.oneDrive.error.saveInfo.${res.data?.reason}`,
                            'common.apiException.internalError',
                          ])
                        );
                      }
                    } else {
                      setErrorMsg(undefined);
                      setSyncDataFailFlag(false);
                    }
                  }}
                />
              ) : (
                <DataSyncing
                  onRefresh={async () => {
                    const res = await ProvisionApi.apiGetProvisionStatus(
                      SERVICE,
                      orgId
                    );
                    if (res.data.status === ProvisionStatusEnum.Provisioned) {
                      setOnedriveStatus(
                        OneDriveGraphProvisionStatus.Provisioned
                      );
                    }
                  }}
                />
              ))}
            {onedriveStatus === OneDriveGraphProvisionStatus.Provisioned && (
              <DataSynced />
            )}
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default OneDriveGraphModal;

const GrantPermissionSteps = ({
  onedriveStatus,
  setOnedriveStatus,
  authenticationInfo,
  orgId,
  setErrorMsg,
}: {
  onedriveStatus: OneDriveGraphProvisionStatus;
  setOnedriveStatus: (status: OneDriveGraphProvisionStatus) => void;
  authenticationInfo: MultiAppsAuthenticationInfoRes | null;
  orgId: string;
  setErrorMsg: (msg: string | undefined) => void;
}) => {
  const { t } = useTranslation('provision');
  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };
  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });

  const onFirstPermission = () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: Service.OneDrive,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const onedriveProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_od'
      );
      if (onedriveProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(onedriveProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.OneDrive,
              code,
              radioGroupRef.current.enabledPolicyType,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.oneDrive.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              const statusRes = await ProvisionApi.apiGetProvisionStatus(
                Service.OneDrive,
                orgId
              );
              const onedriveTokenStatus =
                statusRes.data?.authorization?.apps?.[0]?.status;
              if (onedriveTokenStatus === TokenStatus.Created) {
                setOnedriveStatus(
                  OneDriveGraphProvisionStatus.ProvisioningGrantStep1Done
                );
              } else {
                setErrorMsg(
                  t('provision.service.oneDrive.error.saveInfo.FAILURE;450')
                );
              }
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.oneDrive.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  const onSecondPermission = () => {
    const authUrl = authenticationInfo?.[1]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: App.OneDriveBasic,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const onedriveBasicProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_basic_od'
      );
      if (onedriveBasicProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(onedriveBasicProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.OneDriveBasic,
              code,
              EnabledPolicyTypeEnum.None,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.oneDrive.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setOnedriveStatus(
                OneDriveGraphProvisionStatus.ProvisioningGrantStep2Done
              );
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.oneDrive.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Steps
        direction="vertical"
        current={onedriveStatus - 1}
        items={[
          {
            title: (
              <Text {...titleTextStyle}>
                <Trans
                  i18nKey={
                    'provision.service.oneDrive.details.userConfiguration.STEP1'
                  }
                  ns="provision"
                >
                  placeholder
                  <Link
                    href={getCasOLHLink(
                      CAS_DOC_LINK.provision.oneDrive.userConfiguration,
                      i18next.language
                    )}
                    target="_blank"
                  >
                    placeholder
                  </Link>
                  placeholder
                </Trans>
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                {onedriveStatus <
                OneDriveGraphProvisionStatus.ProvisioningGrantStep1Done ? (
                  <>
                    <Divider variant="dashed" orientation="horizontal" />
                    <Text color={colorStyle.color.secondary}>
                      {t('provision.common.selectPolicyToEnable')}
                    </Text>
                    <EnablePolicyRadioGroup
                      service={Service.OneDrive}
                      ref={radioGroupRef}
                    />
                    <Divider variant="dashed" orientation="horizontal" />
                    <Box>
                      <Button
                        variant="secondary"
                        borderRadius="2rem"
                        onClick={() => {
                          onFirstPermission();
                        }}
                      >
                        {t('provision.button.grantPermission')}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  </Box>
                )}
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  onedriveStatus >=
                  OneDriveGraphProvisionStatus.ProvisioningGrantStep1Done
                }
              />
            ),
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t(
                  'provision.service.oneDrive.details.userConfiguration.STEP2'
                )}
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                <Box>
                  {onedriveStatus <
                  OneDriveGraphProvisionStatus.ProvisioningGrantStep2Done ? (
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      disabled={
                        onedriveStatus !==
                        OneDriveGraphProvisionStatus.ProvisioningGrantStep1Done
                      }
                      onClick={() => {
                        onSecondPermission();
                      }}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  ) : (
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  )}
                </Box>
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  onedriveStatus >=
                  OneDriveGraphProvisionStatus.ProvisioningGrantStep2Done
                }
              />
            ),
          },
        ]}
      />
    </>
  );
};
